import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  List,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MoreVertical
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogsPage = () => {
  const { getAuthHeader, token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [threatFilter, setThreatFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (search) params.append("search", search);
      if (threatFilter !== "all") params.append("threat_level", threatFilter);
      
      const [logsRes, countRes] = await Promise.all([
        axios.get(`${API}/logs?${params.toString()}`, { headers }),
        axios.get(`${API}/logs/count`, { headers })
      ]);
      
      setLogs(logsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      toast.error("Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, threatFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLogs();
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/export/${format}`, {
        headers: getAuthHeader(),
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ip_logs.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const getThreatBadge = (level) => {
    const variants = {
      safe: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      suspicious: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      threat: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return variants[level] || variants.safe;
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="space-y-6" data-testid="logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <List className="w-7 h-7 text-primary" />
            IP Logs
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {totalCount} total records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="export-btn">
                <Download className="w-4 h-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="export-csv">
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("json")} data-testid="export-json">
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchLogs}
            className="gap-2"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search IP, city, country, ISP..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-background font-mono"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" size="sm" data-testid="search-btn">Search</Button>
            </form>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={threatFilter} onValueChange={(v) => { setThreatFilter(v); setPage(0); }}>
                <SelectTrigger className="w-[140px] bg-background" data-testid="threat-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="safe">Safe</SelectItem>
                  <SelectItem value="suspicious">Suspicious</SelectItem>
                  <SelectItem value="threat">Threat</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-card/50 border-border" data-testid="logs-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">IP Address</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Location</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">ISP</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Browser / OS</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden xl:table-cell">Timestamp</th>
                  <th className="text-left px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="7" className="px-4 py-3">
                        <div className="skeleton h-6 w-full" />
                      </td>
                    </tr>
                  ))
                ) : logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-secondary/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/logs/${log.id}`} 
                            className="font-mono text-primary hover:underline"
                            data-testid={`log-link-${log.id}`}
                          >
                            {log.ip_address}
                          </Link>
                          {log.is_blacklisted && (
                            <Badge variant="destructive" className="text-[10px]">BL</Badge>
                          )}
                          {log.is_whitelisted && (
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px]">WL</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground text-sm">
                        {log.city && log.country ? `${log.city}, ${log.country}` : log.country || "—"}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-sm truncate max-w-[150px]">
                        {log.isp || "—"}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-sm">
                        {log.browser || "Unknown"} / {log.os || "Unknown"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs uppercase ${getThreatBadge(log.threat_level)}`}>
                            {log.threat_level}
                          </Badge>
                          {log.is_vpn && (
                            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">VPN</Badge>
                          )}
                          {log.is_proxy && (
                            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">Proxy</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell font-mono text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/logs/${log.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">
                      No logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-mono px-3">
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsPage;
